import React, { useEffect, useRef, useMemo } from 'react';
import { Flex } from '@highereducation/hen-ui';
import { useDynamicExperienceContext } from '../../providers/DynamicExperienceProvider';
import {
  ComponentRenderer,
  getComponentTypeFromStep,
} from '../ComponentRegistry';
import { ExperienceWrapper } from './ExperienceWrapper';
import { StepType } from '../../types/enums';
import type {
  Component,
  MultiQuestion,
  Question,
  Step,
  StepResult,
} from '../../types/types';
import type { FlowState } from '../../providers/dynamicExperienceReducer';
import SideRail from '../SideRail/SideRail';
import { stepHasSideRailContent } from '../SideRail/getSideRailConfigsForStep';
import StepNavigation from './StepNavigation';
import { StepPreSubHeading } from './StepPreSubHeading';
import { StepHeader } from './StepHeader';
import { StepTooltip } from './StepTooltip';
import { StepContent } from './StepContent';
import { validateStepAnswers } from '@de/utils/validateStepAnswers';
import { getConditionalQuestions } from '@de/utils/getConditionalQuestions';
import { Results } from '@de/components/Results';
import { getAllSelectedOptions } from '@de/utils/dataFromFlowUtils';
import { useInfoBanners } from '@de/hooks/useInfoBanners';
import { InfoBanner } from '@de/components/InfoBanner';
import { TrackingProvider, useTracking } from '@de/providers/TrackingProvider';
import {
  newRelicInteractionName,
  newRelicNoticeError,
  newRelicPageAction,
} from '@de/utils/newRelicActions';

export interface ExperienceProps {
  /**
   * Additional props to pass to components
   */
  componentProps?: Record<string, unknown>;
  /**
   * When provided, called instead of rendering the built-in results UI when the
   * flow reaches a RESULT step. Receives the result step config and the user's
   * collected flow answers. Supplying this prop also suppresses all API calls
   * that the built-in ResultsPage makes on mount (getQualifiedResults, lead
   * submission setup, etc.) because the Results component tree never renders.
   */
  onResult?: (
    result: Step & StepResult,
    context: { flowState: FlowState; steps: Step[] },
  ) => void;
}

/**
 * Main Experience component (renamed from Flow). Renders the current
 * step in the dynamic experience flow.
 *
 * Wraps the rendered body in {@link TrackingProvider}, which is the
 * sole owner of step-level lifecycle event firing
 * (`ElementLoaded` / `ProductLoaded` / `StepElementLoaded` on mount,
 * `ProductViewed` / `FormViewed` on viewport intersect — see
 * `useEntryPointEventFiring`). The provider is keyed on the current
 * step id so each step transition mounts a fresh observer and a
 * fresh trigger surface; the shared `firedEntryEventKeys` Set then
 * suppresses re-fires on back-button revisits of the same step.
 *
 * DSL-backed steps (rendered through `ConfigurableStep`) inherit the
 * same `TrackingProvider` context — `ConfigurableStep` no longer
 * mounts its own provider.
 */
export const Experience: React.FC<ExperienceProps> = ({
  componentProps,
  onResult,
}) => {
  const { currentStep, currentStepType, currentStepIndex } =
    useDynamicExperienceContext();

  // When the current step became visible, so the next `deStepView` can report
  // how long the step being left was on screen. This ref lives in `Experience`
  // rather than `ExperienceBody` because the body is re-keyed per step below
  // and therefore remounts on every transition, which would reset it.
  const stepEnteredAtRef = useRef<number | null>(null);

  // Report step progression to New Relic. Keyed on the step id so every
  // navigation path — forward, back, jump, restart, and initial mount — emits
  // exactly one event, without each dispatch site in the provider having to
  // instrument itself.
  useEffect(() => {
    if (!currentStep) return;

    const now = Date.now();
    const previousEnteredAt = stepEnteredAtRef.current;
    stepEnteredAtRef.current = now;

    const flowId = currentStep.flowId ?? 'unknown';
    const stepType = currentStepType ?? 'unknown';

    newRelicPageAction('deStepView', {
      flowId,
      stepId: currentStep.id ?? 'unknown',
      stepType,
      stepIndex: currentStepIndex ?? -1,
      componentType: getComponentTypeFromStep(stepType, currentStep) ?? 'none',
      ...(previousEnteredAt !== null && {
        msSincePreviousStep: now - previousEnteredAt,
      }),
    });

    // Name by step *type*, not step id: ids are GUIDs and
    // `browserInteractionName` is a grouping dimension in the Browser UI.
    // `stepId` stays available as a `deStepView` attribute for faceting.
    newRelicInteractionName(`DE/${flowId}/${stepType}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id, currentStepType]);

  // DSL-backed COMPONENT steps carry `dslConfigId` on the step; pass
  // it through so `TrackingProvider`'s `stepCorrelation` and the
  // dedup-key fallback inside `useEntryPointEventFiring` both see
  // the configured id when `flowId` is absent.
  const dslConfigId = (currentStep as (Step & Partial<Component>) | undefined)
    ?.dslConfigId;
  const dslConfigVersion = (
    currentStep as (Step & Partial<Component>) | undefined
  )?.dslConfig?.version;

  // Re-key on step id so the provider (and the entry-point firing
  // hook + IntersectionObserver inside it) remount per step. Fresh
  // refs + fresh observer per step transition; the shared
  // `firedEntryEventKeys` Set still gates re-fires for back-button
  // revisits of the same step in the same session.
  const providerKey = currentStep?.id ?? '__no-step__';

  return (
    <TrackingProvider
      key={providerKey}
      dslConfigId={dslConfigId}
      dslConfigVersion={dslConfigVersion}
    >
      <ExperienceBody componentProps={componentProps} onResult={onResult} />
    </TrackingProvider>
  );
};

export default Experience;

/**
 * The Experience body — rendered as a child of `TrackingProvider` so
 * it can wire `setEntryRootNode` to the step's outermost DOM
 * container via `useTracking()`. Split out from `Experience` so the
 * provider sits above the consumer (React context contract).
 */
const ExperienceBody: React.FC<ExperienceProps> = ({
  componentProps,
  onResult,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    steps,
    currentStep,
    currentStepType,
    navigationHistory,
    isFullPageExp,
    styleVariant,
    handlePrevious,
    handleStepForward,
    buttonText,
    getStepMetadataById,
    validationErrors,
    eventContext,
    flowState,
  } = useDynamicExperienceContext();

  const tracking = useTracking();
  const setEntryRootNode = tracking?.setEntryRootNode;

  const hasPreviousStep = navigationHistory.length > 1;
  const currentStepId = currentStep?.id;

  const allSelectedOptions = useMemo(
    () => getAllSelectedOptions(flowState),
    [flowState],
  );

  const currentStepData = currentStep?.id
    ? getStepMetadataById(currentStep.id)
    : undefined;

  const { visibleInfoBanners } = useInfoBanners({
    currentStep,
    currentStepData,
    allSelectedOptions,
  });

  const shouldRenderInfoBanners = visibleInfoBanners.some(
    (banner) => banner.config,
  );

  // Keep the latest `onResult` in a ref so the firing effect below isn't
  // re-keyed by consumers passing an inline arrow (which would change
  // identity every render and re-fire the callback).
  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  });

  // Fire `onResult` from an effect — not during render — when the flow
  // transitions to a RESULT step. The previous implementation invoked
  // `onResult` synchronously inside the render branch below, which surfaced
  // React's "Cannot update a component while rendering a different
  // component" warning whenever the consumer's callback enqueued state.
  // Keyed on the step transition (not flowState/steps) so it fires once per
  // RESULT step rather than on every flow-state mutation.
  useEffect(() => {
    if (currentStepType !== StepType.RESULT) return;
    if (!currentStep) return;
    if (!onResultRef.current) return;
    onResultRef.current(currentStep as Step & StepResult, { flowState, steps });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepType, currentStep?.id]);

  useEffect(() => {
    if (!isFullPageExp || !currentStepId) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStepId, isFullPageExp]);

  // Report "unrenderable step" occurrences from an effect — not during
  // render — so a misconfigured step that stays mounted emits the New
  // Relic notice once per step transition instead of on every re-render
  // (and doesn't double-count under StrictMode dev double-render). Keyed
  // on the step id to mirror the RESULT `onResult` effect above.
  useEffect(() => {
    if (!currentStep) return;
    const isRegistryStep =
      currentStepType === StepType.COMPONENT ||
      currentStepType === StepType.DCS ||
      currentStepType === StepType.BANNER_CTA;
    if (!isRegistryStep) return;
    const componentType = getComponentTypeFromStep(
      currentStepType,
      currentStep,
    );
    const dslConfigId = (currentStep as Step & Partial<Component>).dslConfigId;
    if (componentType || dslConfigId) return;
    // A COMPONENT/DCS/BANNER_CTA step that resolves to neither a registered
    // component type nor a linked DSL config renders nothing. Surface it
    // through the shared New Relic seam so the misconfigured flow is visible
    // in monitoring instead of silently blanking the step.
    newRelicNoticeError(
      new Error('Step rendered nothing: no componentType or dslConfigId'),
      {
        source: 'Experience',
        stepType: currentStepType,
        stepId: currentStep.id ?? 'unknown',
        flowId: currentStep.flowId ?? 'unknown',
        // Key names only, never values. `currentStep` here is a
        // placeholder-translated copy, and the translation source
        // (`getCurrentFieldValues`) is not run through `isPii`, so any
        // authored string on the step can hold a lead answer once a
        // placeholder resolves.
        configKeys: Object.keys(currentStep).sort().join(','),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId, currentStepType]);

  if (!currentStep) {
    return null;
  }

  // Handle component/DCS/Banner types via internal component registry.
  //
  // `setEntryRootNode` wiring:
  // - `COMPONENT`: intentionally NOT attached here. The DSL render
  //   path (`ConfigurableStep`'s `EntryRoot`) wraps the DSL subtree
  //   and wires the ref one level deeper, so the IO observes the
  //   actual DSL content box rather than this thin wrapper.
  // - `DCS` / `BANNER_CTA`: non-DSL step types rendered straight
  //   through `ComponentRenderer` with no inner ref wiring. Attach
  //   the ref to this wrapper so `useEntryPointEventFiring`'s IO
  //   observer has a real layout box to watch — without this, entry
  //   DCS/Banner steps miss `ProductViewed` / `FormViewed`, and
  //   non-entry DCS/Banner steps miss `ElementViewed`.
  if (
    currentStepType === StepType.COMPONENT ||
    currentStepType === StepType.DCS ||
    currentStepType === StepType.BANNER_CTA
  ) {
    const componentType = getComponentTypeFromStep(
      currentStepType,
      currentStep,
    );
    // DSL-backed COMPONENT steps carry `dslConfigId` instead of `componentType`.
    // Let ComponentRenderer handle the DSL short-circuit rather than bailing here.
    const dslConfigId = (currentStep as Step & Partial<Component>).dslConfigId;

    if (!componentType && !dslConfigId) {
      // Renders nothing; the New Relic notice for this case is emitted from
      // the effect above (once per step) rather than during render.
      return null;
    }

    const shouldAttachEntryRoot = currentStepType !== StepType.COMPONENT;

    return (
      <div
        className='de-dynamic-experience col-span-full w-full'
        data-step-type={currentStepType}
        ref={shouldAttachEntryRoot ? setEntryRootNode : undefined}
      >
        <ComponentRenderer
          componentType={componentType ?? ''}
          currentStep={currentStep}
          componentProps={componentProps}
        />
      </div>
    );
  }

  // Result type (supersonic does not attach a step root to result steps,
  // and result pages do not participate in step-level event firing).
  //
  // When `onResult` is supplied, the consumer renders the result; the
  // callback is invoked from the effect above on RESULT-step transition,
  // and this branch renders nothing.
  if (currentStepType === StepType.RESULT) {
    if (onResult) return null;
    return (
      <Flex direction='column' className='flex-1'>
        <Results {...(currentStep as Step & StepResult)} />
      </Flex>
    );
  }

  // Question/MultiQuestion types
  const isStepRequired = currentStep?.required ?? false;
  const hasErrors = validationErrors.length > 0;
  const hasValidAnswers =
    currentStepType === StepType.MULTI_QUESTION
      ? (() => {
          const mq = currentStep as Step & MultiQuestion;
          if (!mq.stepQuestions) return true;
          const visible = getConditionalQuestions(
            mq.stepQuestions,
            getStepMetadataById,
          );
          return visible.every(
            (q: Question) =>
              !q.required ||
              validateStepAnswers(getStepMetadataById(q.id), hasErrors),
          );
        })()
      : currentStepType === StepType.QUESTION
        ? validateStepAnswers(
            getStepMetadataById(currentStep.id || ''),
            hasErrors,
          )
        : true;
  const nextDisabled = isStepRequired && !hasValidAnswers;

  const hasSideRail = isFullPageExp && stepHasSideRailContent(currentStep);

  return (
    <div
      // Page-frame padding (horizontal gutter + top/bottom rhythm) is only the
      // experience's responsibility when embedded. Full-page hosts (Discover)
      // own the frame via hen-ui page tokens (`px-page-sides pt-page-top
      // pb-page-bottom`) on their `<main>`, so we drop it here to avoid
      // doubling up and to keep QUESTION steps aligned with COMPONENT/RESULT.
      className={`de-experience bg-white flex flex-col md:flex-row w-full md:justify-start md:items-stretch ${
        hasSideRail ? 'gap-6 md:gap-section-break' : 'gap-section-break'
      } ${isFullPageExp ? '' : 'px-4 lg:px-16 pt-8 md:pt-14 pb-14 md:pb-28'}`}
      ref={setEntryRootNode}
    >
      <ExperienceWrapper contentRef={contentRef}>
        <Flex
          direction='column'
          gap='md'
          className={`de-question-container md:gap-lg ${
            isFullPageExp ? '' : 'p-4'
          } ${styleVariant === 'alt' ? 'de-alt-variant' : ''}`}
          ref={contentRef}
        >
          <StepPreSubHeading step={currentStep} isFullPageExp={isFullPageExp} />
          <StepHeader step={currentStep} isFullPageExp={isFullPageExp} />
          <StepTooltip step={currentStep} />
          <StepContent step={currentStep} stepType={currentStepType} />
          {shouldRenderInfoBanners && (
            <div className='flex flex-col gap-3'>
              {visibleInfoBanners.map((config) => {
                if (!config.config) return null;
                return (
                  <InfoBanner
                    key={config.id}
                    {...config.config}
                    flowState={flowState}
                    eventContext={eventContext}
                  />
                );
              })}
            </div>
          )}
          <StepNavigation
            onBack={handlePrevious}
            onNext={handleStepForward}
            nextLabel={buttonText}
            showBack={hasPreviousStep}
            isFullPageExp={isFullPageExp}
            disabled={nextDisabled}
          />
        </Flex>
      </ExperienceWrapper>

      {hasSideRail && <SideRail />}
    </div>
  );
};
